import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import SendIcon from '@mui/icons-material/Send'
import DescriptionIcon from '@mui/icons-material/Description'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import MessageIcon from '@mui/icons-material/Message'
import { useSearchParams } from 'react-router-dom'
import ClaimCard from 'pages/MyAccount/ClaimCard'
import {
  uploadFile,
  MessageAttachment,
  SendMessageParams,
  Claim as ClaimData,
  createClaim,
  sendMessage,
  ClaimItem,
  ClaimType,
  Delivery,
  ClaimPart,
  ClaimStatus
} from 'api/claims'
import { Order, OrderItem } from 'hooks/useOrder'
import { PRODUCT_IMAGE_BASE_URL } from 'config'
import { formatToTitleCase } from 'utils/formatUtils'

interface ClaimProps {
  claims: ClaimData[]
  orders: Order[]
  onRefresh: () => Promise<void>
  fetchClaimById: (claimId: string) => Promise<ClaimData | null>
  sendClaimMessage: (
    claimId: string,
    params: SendMessageParams
  ) => Promise<boolean>
  isLoading?: boolean
}

interface PendingFile {
  file: File
  fileName: string
}

interface SelectedItem extends ClaimItem {
  orderItem: OrderItem
}

const claimTypes: ClaimType[] = ['Exchange', 'Credit']

const deliveryOptions: Delivery[] = [
  'Pick Up',
  'Curbside Delivery (Up to 50 miles)',
  'Garage/Driveway Delivery (Up to 50 miles)',
  'Free delivery charges',
  'RL Carriers',
  'Fedex International',
  'Fedex US LTL'
]

const claimParts: ClaimPart[] = [
  'Door',
  'Drawer Front',
  'Frame',
  'Line',
  'Left Side Panel',
  'Right Side Panel',
  'Bottom Panel',
  'Top Panel',
  'Slides',
  'Hinge',
  'Spinners(Lazy Susan)',
  'Shelf Clips',
  'Drawer Front for CSB',
  '2BC',
  'Other(Explain in the notes)',
  'Drawer Front Bottom',
  'Back Panel'
]

const Claim: React.FC<ClaimProps> = ({
  claims,
  orders,
  onRefresh,
  fetchClaimById,
  sendClaimMessage,
  isLoading = false
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [error, setError] = useState('')
  const [selectedClaim, setSelectedClaim] = useState<ClaimData | null>(null)
  const [viewingClaim, setViewingClaim] = useState(false)
  const [replyMessage, setReplyMessage] = useState('')
  const [replyFiles, setReplyFiles] = useState<PendingFile[]>([])
  const [sendingReply, setSendingReply] = useState(false)
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [dialogMessage, setDialogMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // States for creating new claim
  const [activeTab, setActiveTab] = useState(0)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([])
  const [message, setMessage] = useState('')
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const claimId = searchParams.get('id')
    if (claimId && claims.length > 0) {
      const existingClaim = claims.find(ro => ro.id === claimId)
      if (existingClaim && !viewingClaim) {
        setSelectedClaim(existingClaim)
        setViewingClaim(true)
        setReplyMessage('')
        setReplyFiles([])
      }
    }
  }, [searchParams, claims, viewingClaim])

  const handleViewClaim = async (claimId: string) => {
    setSearchParams({ tab: 'claim', id: claimId })

    const existingClaim = claims.find(ro => ro.id === claimId)

    if (existingClaim) {
      setSelectedClaim(existingClaim)
      setViewingClaim(true)
      setReplyMessage('')
      setReplyFiles([])
    } else {
      const claim = await fetchClaimById(claimId)
      if (claim) {
        setSelectedClaim(claim)
        setViewingClaim(true)
        setReplyMessage('')
        setReplyFiles([])
      } else {
        setError('Failed to load claim details')
      }
    }
  }

  const handleReplyFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const newFiles: PendingFile[] = []

    for (let i = 0; i < files.length; i++) {
      newFiles.push({
        file: files[i],
        fileName: files[i].name
      })
    }

    const currentTotalSize = replyFiles.reduce((sum, f) => sum + f.file.size, 0)
    const newTotalSize = newFiles.reduce((sum, f) => sum + f.file.size, 0)
    const totalSize = currentTotalSize + newTotalSize

    if (totalSize > 5 * 1024 * 1024) {
      setError(
        `Total file size must be less than 5MB. Current: ${(
          currentTotalSize / 1024
        ).toFixed(1)}KB, Adding: ${(newTotalSize / 1024).toFixed(1)}KB`
      )
      return
    }

    setReplyFiles(prev => [...prev, ...newFiles])
    setError('')

    event.target.value = ''
  }

  const handleRemoveReplyFile = (index: number) => {
    setReplyFiles(prev => prev.filter((_, idx) => idx !== index))
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSendReply = async () => {
    if (!selectedClaim) return

    if (!replyMessage.trim()) {
      setError('Please provide a message')
      return
    }

    setSendingReply(true)
    setError('')

    try {
      const uploadedAttachments: MessageAttachment[] = []

      if (replyFiles.length > 0) {
        for (const replyFile of replyFiles) {
          const uploadResult = await uploadFile(replyFile.file)

          if (!uploadResult.success || !uploadResult.fileUrl) {
            setSendingReply(false)
            setError(
              `Failed to upload ${replyFile.fileName}: ${
                uploadResult.error || 'Unknown error'
              }`
            )
            return
          }

          uploadedAttachments.push({
            fileName: replyFile.fileName,
            fileUrl: uploadResult.fileUrl
          })
        }
      }

      const success = await sendClaimMessage(selectedClaim.id, {
        message: replyMessage.trim(),
        attachments:
          uploadedAttachments.length > 0 ? uploadedAttachments : undefined
      })

      setSendingReply(false)

      if (success) {
        const newMessage: any = {
          id: `temp-${Date.now()}`,
          claimId: selectedClaim.id,
          message: replyMessage.trim(),
          senderType: 'CUSTOMER' as const,
          senderId: selectedClaim.accountId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          attachments: uploadedAttachments,
          Account: {
            companyName: '',
            email: ''
          }
        }

        setSelectedClaim(prev => {
          if (!prev) return prev
          return {
            ...prev,
            ClaimMessages: [...(prev.ClaimMessages || []), newMessage]
          }
        })

        setReplyMessage('')
        setReplyFiles([])

        setSuccessDialogOpen(true)

        setTimeout(() => scrollToBottom(), 100)

        onRefresh()
      } else {
        setError('Failed to send message')
      }
    } catch (err: any) {
      setSendingReply(false)
      setError(err.message || 'An unexpected error occurred')
    }
  }

  // Handlers for creating new claim
  const handleOrderSelect = (order: Order) => {
    setSelectedOrder(order)
    setSelectedItems([])
    setMessage('')
    setPendingFiles([])
    setError('')
  }

  const handleBackFromOrderSelection = () => {
    setSelectedOrder(null)
    setSelectedItems([])
    setMessage('')
    setPendingFiles([])
    setError('')
  }

  const handleItemSelect = (orderItem: OrderItem, checked: boolean) => {
    if (checked) {
      setSelectedItems([
        ...selectedItems,
        {
          orderItemId: orderItem.id,
          quantity: 1,
          claimType: '' as ClaimType,
          delivery: '' as Delivery,
          claimPart: '' as ClaimPart,
          orderItem
        }
      ])
    } else {
      setSelectedItems(
        selectedItems.filter(item => item.orderItemId !== orderItem.id)
      )
    }
  }

  const handleQuantityChange = (orderItemId: string, quantity: number) => {
    setSelectedItems(prev =>
      prev.map(item =>
        item.orderItemId === orderItemId ? { ...item, quantity } : item
      )
    )
  }

  const handleClaimTypeChange = (orderItemId: string, claimType: ClaimType) => {
    setSelectedItems(prev =>
      prev.map(item =>
        item.orderItemId === orderItemId ? { ...item, claimType } : item
      )
    )
  }

  const handleDeliveryChange = (orderItemId: string, delivery: Delivery) => {
    setSelectedItems(prev =>
      prev.map(item =>
        item.orderItemId === orderItemId ? { ...item, delivery } : item
      )
    )
  }

  const handleClaimPartChange = (orderItemId: string, claimPart: ClaimPart) => {
    setSelectedItems(prev =>
      prev.map(item =>
        item.orderItemId === orderItemId ? { ...item, claimPart } : item
      )
    )
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const newFiles: PendingFile[] = []
    for (let i = 0; i < files.length; i++) {
      newFiles.push({
        file: files[i],
        fileName: files[i].name
      })
    }

    const currentTotalSize = pendingFiles.reduce(
      (sum, f) => sum + f.file.size,
      0
    )
    const newTotalSize = newFiles.reduce((sum, f) => sum + f.file.size, 0)
    const totalSize = currentTotalSize + newTotalSize

    if (totalSize > 5 * 1024 * 1024) {
      setError(
        `Total file size must be less than 5MB. Current: ${(
          currentTotalSize / 1024
        ).toFixed(1)}KB, Adding: ${(newTotalSize / 1024).toFixed(1)}KB`
      )
      return
    }

    setPendingFiles([...pendingFiles, ...newFiles])
    event.target.value = ''
  }

  const handleRemoveFile = (fileName: string) => {
    setPendingFiles(pendingFiles.filter(f => f.fileName !== fileName))
  }

  const handleSubmitReturn = async () => {
    if (!selectedOrder) return

    if (selectedItems.length === 0) {
      setError('Please select at least one item to claim')
      return
    }

    // Validate that all items have required fields filled
    const incompleteItems = selectedItems.filter(
      item => !item.claimType || !item.delivery || !item.claimPart
    )
    if (incompleteItems.length > 0) {
      setError(
        'Please fill in all required fields (Claim Type, Delivery, and Claim Part) for all selected items'
      )
      return
    }

    if (!message.trim()) {
      setError('Please provide a message')
      return
    }

    setSubmitting(true)
    setError('')

    const claimData = {
      orderId: selectedOrder.id,
      items: selectedItems.map(item => ({
        orderItemId: item.orderItemId,
        quantity: item.quantity,
        claimType: item.claimType,
        delivery: item.delivery,
        claimPart: item.claimPart
      }))
    }

    const result = await createClaim(claimData)

    if (result.success && result.claimId) {
      const uploadedFiles = []

      for (const pendingFile of pendingFiles) {
        const uploadResult = await uploadFile(pendingFile.file)
        if (uploadResult.success && uploadResult.fileUrl) {
          uploadedFiles.push({
            fileName: pendingFile.fileName,
            fileUrl: uploadResult.fileUrl
          })
        }
      }

      const messageResult = await sendMessage(result.claimId, {
        message,
        attachments: uploadedFiles
      })

      setSubmitting(false)

      if (messageResult.success) {
        setDialogMessage(
          'Claim request submitted successfully! You can view it in the "View Claims" tab.'
        )
        setSuccessDialogOpen(true)
        await onRefresh()
      } else {
        setDialogMessage(
          'Claim created but failed to send message: ' +
            (messageResult.error || 'Unknown error')
        )
        setErrorDialogOpen(true)
      }
    } else {
      setSubmitting(false)
      setDialogMessage(result.error || 'Failed to create claim')
      setErrorDialogOpen(true)
    }
  }

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false)
    setActiveTab(0)
    handleBackFromOrderSelection()
  }

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false)
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!viewingClaim && (
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab sx={{ textTransform: 'none' }} label='View Claims' />
          <Tab sx={{ textTransform: 'none' }} label='Create New Claim' />
        </Tabs>
      )}

      {viewingClaim && selectedClaim ? (
        <Box>
          <Button
            variant='outlined'
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setViewingClaim(false)
              setSelectedClaim(null)
              setReplyMessage('')
              setReplyFiles([])
              setSearchParams({ tab: 'claim' })
            }}
            sx={{
              mb: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem'
            }}
          >
            Go Back
          </Button>

          <Card
            elevation={2}
            sx={{
              mb: 4,
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}
              >
                <Box>
                  <Typography
                    variant='overline'
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      letterSpacing: 1.5
                    }}
                  >
                    Claim Request
                  </Typography>
                  <Typography
                    variant='h4'
                    fontWeight='bold'
                    sx={{ color: 'primary.main', mt: 0.5 }}
                  >
                    #{selectedClaim.Order.netsuiteSalesOrderId}
                  </Typography>
                </Box>
                <Chip
                  label={formatToTitleCase(selectedClaim.status)}
                  color={
                    selectedClaim.status === ClaimStatus.APPROVED
                      ? 'success'
                      : selectedClaim.status === ClaimStatus.REJECTED
                      ? 'error'
                      : selectedClaim.status === ClaimStatus.COMPLETED
                      ? 'default'
                      : selectedClaim.status === ClaimStatus.PENDING
                      ? 'info'
                      : 'warning'
                  }
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    px: 2,
                    py: 2.5,
                    height: 'auto'
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant='h6'
                fontWeight='bold'
                sx={{ mb: 3, color: 'text.primary' }}
              >
                Items to Claim
              </Typography>
              {selectedClaim.ClaimItems.map((item, index) => (
                <Box key={item.id}>
                  {index > 0 && <Divider sx={{ my: 3 }} />}
                  <Box
                    sx={{
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      p: 2.5,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Grid container spacing={1.5} alignItems='center'>
                      <Grid item xs={3}>
                        <Typography
                          variant='caption'
                          color='textSecondary'
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5
                          }}
                        >
                          Product
                        </Typography>
                        <Typography
                          variant='h6'
                          fontWeight='bold'
                          sx={{ mt: 0.5, mb: 0.5 }}
                        >
                          {item.OrderItem.Product.sku}
                        </Typography>
                        <Typography variant='body2' color='textSecondary'>
                          {item.OrderItem.Product.color}
                        </Typography>
                      </Grid>
                      <Grid item xs={1.5}>
                        <Typography
                          variant='caption'
                          color='textSecondary'
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5
                          }}
                        >
                          Quantity
                        </Typography>
                        <Typography
                          variant='h6'
                          fontWeight='bold'
                          sx={{ mt: 0.5 }}
                        >
                          {item.quantity}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography
                          variant='caption'
                          color='textSecondary'
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            mb: 1,
                            display: 'block'
                          }}
                        >
                          Claim Type
                        </Typography>
                        <Chip
                          label={item.claimType}
                          size='small'
                          color='primary'
                          variant='outlined'
                          sx={{ fontWeight: 600 }}
                        />
                      </Grid>
                      <Grid item xs={2.5}>
                        <Typography
                          variant='caption'
                          color='textSecondary'
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5
                          }}
                        >
                          Part
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{ mt: 0.5, fontWeight: 500 }}
                        >
                          {item.claimPart}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography
                          variant='caption'
                          color='textSecondary'
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5
                          }}
                        >
                          Delivery
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{ mt: 0.5, fontWeight: 500 }}
                        >
                          {item.delivery}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>

          <Card elevation={2}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant='h6'
                fontWeight='bold'
                sx={{ mb: 3, color: 'text.primary' }}
              >
                Conversation
              </Typography>

              <Box
                sx={{
                  mb: 3,
                  maxHeight: '500px',
                  overflowY: 'auto',
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  p: 2
                }}
              >
                {selectedClaim.ClaimMessages &&
                selectedClaim.ClaimMessages.length > 0 ? (
                  [...selectedClaim.ClaimMessages]
                    .sort(
                      (a, b) =>
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime()
                    )
                    .map(msg => (
                      <Box key={msg.id} sx={{ mb: 2.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent:
                              msg.senderType === 'CUSTOMER'
                                ? 'flex-end'
                                : 'flex-start',
                            mb: 1
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '75%',
                              bgcolor:
                                msg.senderType === 'CUSTOMER'
                                  ? 'primary.main'
                                  : 'white',
                              color:
                                msg.senderType === 'CUSTOMER'
                                  ? 'white'
                                  : 'text.primary',
                              borderRadius: 3,
                              p: 2.5,
                              boxShadow: msg.senderType === 'CUSTOMER' ? 2 : 1,
                              border:
                                msg.senderType === 'CUSTOMER'
                                  ? 'none'
                                  : '1px solid',
                              borderColor: 'grey.200'
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 1,
                                gap: 2
                              }}
                            >
                              <Typography
                                variant='caption'
                                sx={{
                                  fontWeight: 'bold',
                                  color:
                                    msg.senderType === 'CUSTOMER'
                                      ? 'white'
                                      : 'primary.main'
                                }}
                              >
                                {msg.senderType === 'CUSTOMER'
                                  ? 'You'
                                  : 'Support Team'}
                                {msg.Account &&
                                  (msg.senderType === 'ADMIN'
                                    ? ` - ${msg.Account.firstName || ''} ${
                                        msg.Account.lastName || ''
                                      }`.trim()
                                    : ` - ${msg.Account.companyName}`)}
                              </Typography>
                              <Typography
                                variant='caption'
                                sx={{
                                  color:
                                    msg.senderType === 'CUSTOMER'
                                      ? 'rgba(255,255,255,0.7)'
                                      : 'text.secondary',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {new Date(msg.createdAt).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography
                              variant='body2'
                              sx={{ whiteSpace: 'pre-wrap' }}
                            >
                              {msg.message}
                            </Typography>
                            {msg.attachments && msg.attachments.length > 0 && (
                              <Box
                                sx={{
                                  mt: 1,
                                  pt: 1,
                                  borderTop: 1,
                                  borderColor:
                                    msg.senderType === 'CUSTOMER'
                                      ? 'rgba(255,255,255,0.3)'
                                      : 'divider'
                                }}
                              >
                                <Typography
                                  variant='caption'
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mb: 0.5,
                                    color:
                                      msg.senderType === 'CUSTOMER'
                                        ? 'rgba(255,255,255,0.9)'
                                        : 'text.secondary'
                                  }}
                                >
                                  <DescriptionIcon sx={{ fontSize: 14 }} />
                                  Attachments:
                                </Typography>
                                {msg.attachments.map((att, idx) => (
                                  <Link
                                    key={idx}
                                    href={att.fileUrl}
                                    target='_blank'
                                    rel='noopener'
                                    sx={{
                                      display: 'block',
                                      fontSize: '0.75rem',
                                      color:
                                        msg.senderType === 'CUSTOMER'
                                          ? 'white'
                                          : 'primary.main',
                                      textDecoration: 'underline',
                                      '&:hover': { textDecoration: 'none' }
                                    }}
                                  >
                                    {att.fileName}
                                  </Link>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      color: 'text.secondary'
                    }}
                  >
                    <Typography variant='body2'>
                      No messages yet. Start the conversation below.
                    </Typography>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {selectedClaim.status !== ClaimStatus.COMPLETED && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      p: 3,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Typography
                      variant='subtitle1'
                      fontWeight='bold'
                      sx={{ mb: 2, color: 'text.primary' }}
                    >
                      Send a Reply
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      placeholder='Type your message here...'
                      value={replyMessage}
                      onChange={e => setReplyMessage(e.target.value)}
                      disabled={sendingReply}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                    />

                    <Box sx={{ mb: 2 }}>
                      <input
                        accept='image/*,.pdf,.doc,.docx'
                        style={{ display: 'none' }}
                        id='reply-attachment-file-input'
                        type='file'
                        multiple
                        onChange={handleReplyFileSelect}
                        disabled={sendingReply}
                      />
                      <label htmlFor='reply-attachment-file-input'>
                        <Button
                          variant='outlined'
                          component='span'
                          startIcon={<AttachFileIcon />}
                          disabled={sendingReply}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.25,
                            borderWidth: 2,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            '&:hover': {
                              borderWidth: 2,
                              bgcolor: 'primary.50',
                              transform: 'translateY(-1px)',
                              boxShadow: 1
                            },
                            transition: 'all 0.2s'
                          }}
                        >
                          Add Attachments
                        </Button>
                      </label>
                      <Typography
                        variant='caption'
                        display='block'
                        sx={{
                          mt: 1.5,
                          color: 'text.secondary',
                          fontSize: '0.75rem'
                        }}
                      >
                        Supported: Images, PDF, DOC, DOCX (Max 5MB total)
                      </Typography>
                    </Box>

                    {replyFiles.length > 0 && (
                      <Card
                        variant='outlined'
                        sx={{ mb: 2, bgcolor: 'grey.50' }}
                      >
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography
                            variant='caption'
                            color='textSecondary'
                            sx={{ mb: 1, display: 'block' }}
                          >
                            Files to Upload ({replyFiles.length}) - Total:{' '}
                            {(
                              replyFiles.reduce(
                                (sum, f) => sum + f.file.size,
                                0
                              ) / 1024
                            ).toFixed(1)}{' '}
                            KB
                          </Typography>
                          <List dense disablePadding>
                            {replyFiles.map((file, index) => (
                              <ListItem key={index} sx={{ px: 0 }}>
                                <ListItemText
                                  primary={file.fileName}
                                  secondary={`${(file.file.size / 1024).toFixed(
                                    1
                                  )} KB`}
                                  primaryTypographyProps={{
                                    variant: 'body2'
                                  }}
                                  secondaryTypographyProps={{
                                    variant: 'caption'
                                  }}
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge='end'
                                    size='small'
                                    onClick={() => handleRemoveReplyFile(index)}
                                    disabled={sendingReply}
                                  >
                                    <DeleteIcon fontSize='small' />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    )}

                    <Button
                      variant='contained'
                      color='primary'
                      onClick={handleSendReply}
                      disabled={sendingReply || !replyMessage.trim()}
                      startIcon={
                        sendingReply ? (
                          <CircularProgress size={20} color='inherit' />
                        ) : (
                          <SendIcon />
                        )
                      }
                      fullWidth
                      sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        boxShadow: 2,
                        '&:hover': {
                          boxShadow: 4
                        }
                      }}
                    >
                      {sendingReply ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      ) : activeTab === 0 ? (
        <Box>
          <Typography variant='h5' fontWeight='bold' sx={{ mb: 3 }}>
            Your Claims
          </Typography>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
              }}
            >
              <CircularProgress />
            </Box>
          ) : claims.length === 0 ? (
            <Card>
              <CardContent>
                <Alert severity='info'>
                  You haven&apos;t submitted any claims yet. Switch to &quot;Create New
                  Return&quot; to submit your first return request.
                </Alert>
              </CardContent>
            </Card>
          ) : (
            <Grid container>
              {claims.map(claim => (
                <Grid item xs={12} key={claim.id}>
                  <ClaimCard claim={claim} onClick={handleViewClaim} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ) : (
        <Box>
          {!selectedOrder ? (
            <Box>
              <Typography variant='h5' fontWeight='bold' sx={{ mb: 3 }}>
                Select an Order to Create Claim
              </Typography>
              {orders.length === 0 ? (
                <Card>
                  <CardContent>
                    <Alert severity='info'>
                      You don&apos;t have any orders yet. Place an order first to
                      request a claim.
                    </Alert>
                  </CardContent>
                </Card>
              ) : (
                <Grid container spacing={2}>
                  {orders.map(order => (
                    <Grid item xs={12} key={order.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-2px)'
                          }
                        }}
                        onClick={() => handleOrderSelect(order)}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <Box>
                              <Typography variant='h6' fontWeight='bold'>
                                Order #{order.salesOrderNumber}
                              </Typography>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                {new Date(order.createdAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Typography variant='h6' fontWeight='bold'>
                              ${order.finalAmount.toFixed(2)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          ) : (
            <Box>
              <Box
                onClick={handleBackFromOrderSelection}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  cursor: 'pointer',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    '& .back-arrow': {
                      transform: 'translateX(-4px)'
                    },
                    '& .back-text': {
                      color: 'primary.main'
                    }
                  }
                }}
              >
                <ArrowBackIcon
                  className='back-arrow'
                  sx={{
                    color: 'primary.main',
                    fontSize: 20,
                    transition: 'transform 0.2s'
                  }}
                />
                <Typography
                  className='back-text'
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    transition: 'color 0.2s'
                  }}
                >
                  Back to Order Selection
                </Typography>
              </Box>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}
              >
                <ReceiptLongIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant='h4' sx={{ fontWeight: 700 }}>
                  Request Claim for Order #{selectedOrder.salesOrderNumber}
                </Typography>
              </Box>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 4 }}>
                Select items you want to claim and provide details
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
                >
                  <ShoppingBagIcon
                    sx={{ color: 'primary.main', fontSize: 22 }}
                  />
                  <Typography variant='h6' sx={{ fontWeight: 700 }}>
                    Select Items to Claim
                  </Typography>
                </Box>
                {selectedOrder.OrderItems.map(item => {
                  const isSelected = selectedItems.some(
                    si => si.orderItemId === item.id
                  )
                  const selected = selectedItems.find(
                    si => si.orderItemId === item.id
                  )

                  return (
                    <Box key={item.id} sx={{ mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={e =>
                              handleItemSelect(item, e.target.checked)
                            }
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2
                            }}
                          >
                            <Box
                              component='img'
                              src={`${PRODUCT_IMAGE_BASE_URL}${item.imageIndex}.png`}
                              alt={item.sku}
                              sx={{
                                width: 60,
                                height: 60,
                                objectFit: 'contain'
                              }}
                            />
                            <Box>
                              <Typography variant='body1' fontWeight='600'>
                                {item.sku}
                              </Typography>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                {item.color}
                                {item.isAssembled &&
                                  (item.hingeDirection
                                    ? `, Assembled (${formatToTitleCase(
                                        item.hingeDirection
                                      )})`
                                    : ', Assembled')}
                              </Typography>
                              <Typography variant='body2'>
                                Ordered Quantity: {item.quantity} | Price: $
                                {parseFloat(item.unitPrice).toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />

                      {isSelected && selected && (
                        <Box sx={{ mt: 2, ml: 4 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={3}>
                              <FormControl fullWidth size='small'>
                                <InputLabel>Claim Quantity *</InputLabel>
                                <Select
                                  value={selected.quantity}
                                  label='Claim Quantity *'
                                  onChange={e =>
                                    handleQuantityChange(
                                      item.id,
                                      e.target.value as number
                                    )
                                  }
                                >
                                  {Array.from(
                                    { length: item.quantity },
                                    (_, i) => i + 1
                                  ).map(qty => (
                                    <MenuItem key={qty} value={qty}>
                                      {qty}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                              <FormControl fullWidth size='small'>
                                <InputLabel>Claim Type *</InputLabel>
                                <Select
                                  value={selected.claimType}
                                  label='Claim Type *'
                                  onChange={e =>
                                    handleClaimTypeChange(
                                      item.id,
                                      e.target.value as ClaimType
                                    )
                                  }
                                >
                                  <MenuItem value='' disabled>
                                    Select claim type
                                  </MenuItem>
                                  {claimTypes.map(type => (
                                    <MenuItem key={type} value={type}>
                                      {type}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                              <FormControl fullWidth size='small'>
                                <InputLabel>Delivery *</InputLabel>
                                <Select
                                  value={selected.delivery}
                                  label='Delivery *'
                                  onChange={e =>
                                    handleDeliveryChange(
                                      item.id,
                                      e.target.value as Delivery
                                    )
                                  }
                                >
                                  <MenuItem value='' disabled>
                                    Select delivery method
                                  </MenuItem>
                                  {deliveryOptions.map(opt => (
                                    <MenuItem key={opt} value={opt}>
                                      {opt}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                              <FormControl fullWidth size='small'>
                                <InputLabel>Claim Part *</InputLabel>
                                <Select
                                  value={selected.claimPart}
                                  label='Claim Part *'
                                  onChange={e =>
                                    handleClaimPartChange(
                                      item.id,
                                      e.target.value as ClaimPart
                                    )
                                  }
                                >
                                  <MenuItem value='' disabled>
                                    Select claim part
                                  </MenuItem>
                                  {claimParts.map(part => (
                                    <MenuItem key={part} value={part}>
                                      {part}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  )
                })}
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
                >
                  <MessageIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                  <Typography variant='h6' sx={{ fontWeight: 700 }}>
                    Additional Information
                  </Typography>
                </Box>
                <TextField
                  label='Message *'
                  multiline
                  rows={4}
                  fullWidth
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder='Please describe the reason for claim...'
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2 }}>
                  <Button
                    variant='outlined'
                    component='label'
                    startIcon={<AttachFileIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.25,
                      borderWidth: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      '&:hover': {
                        borderWidth: 2,
                        bgcolor: 'primary.50',
                        transform: 'translateY(-1px)',
                        boxShadow: 1
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    Add Attachments
                    <input
                      type='file'
                      multiple
                      hidden
                      accept='image/*,.pdf'
                      onChange={handleFileSelect}
                    />
                  </Button>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ ml: 2 }}
                  >
                    Max 5MB total
                  </Typography>
                </Box>

                {pendingFiles.length > 0 && (
                  <List>
                    {pendingFiles.map(pf => (
                      <ListItem key={pf.fileName}>
                        <ListItemText
                          primary={pf.fileName}
                          secondary={`${(pf.file.size / 1024).toFixed(1)} KB`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge='end'
                            onClick={() => handleRemoveFile(pf.fileName)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant='outlined'
                  onClick={handleBackFromOrderSelection}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  onClick={handleSubmitReturn}
                  disabled={submitting || selectedItems.length === 0}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
                    }
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Claim Request'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={successDialogOpen} onClose={handleSuccessDialogClose}>
        <DialogTitle>Success!</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose} color='primary'>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={errorDialogOpen} onClose={handleErrorDialogClose}>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
          Error
        </DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogClose} color='primary'>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Claim
